import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/seo";
import { PageShell, Panel } from "../components/PageShell";
import { PopButton } from "../components/PopButton";

type Msg = { id: string; name: string; email: string; message: string; read: boolean; created_at: string; email_status?: string };

function MessageCard({ m, onChange }: { m: Msg; onChange: () => void }) {
  const act = async (fn: () => Promise<unknown>) => { try { await fn(); onChange(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Panel className={`!p-3 ${m.read ? "opacity-70" : "!border-[#FF5D1F]"}`}>
      <div data-testid={`message-${m.id}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="font-comic text-base">{!m.read && <span className="mr-1 rounded bg-[#FF5D1F] px-1.5 text-[10px] text-white">NEW</span>}{m.name} · <a className="text-[#FF5D1F] underline" href={`mailto:${m.email}`}>{m.email}</a></p>
          <p className="text-[11px] font-bold text-[#6b3a12]">{new Date(m.created_at).toLocaleString("en-GB")}{m.email_status && <span className={`ml-1.5 rounded px-1.5 py-0.5 ${m.email_status === "sent" ? "bg-[#E3F7EA] text-[#0E7A3E]" : m.email_status === "pending" ? "bg-[#FFF5E1]" : "bg-[#FFE0E0] text-[#C8102E]"}`} data-testid={`message-email-status-${m.id}`}>{m.email_status === "sent" ? "Email alert sent" : m.email_status === "pending" ? "Sending alert…" : m.email_status.includes("insufficient_credits") ? "Email alert paused — add Emergent credits" : "Email alert failed"}</span>}</p>
        </div>
        <p className="mt-1 whitespace-pre-wrap text-sm font-semibold">{m.message}</p>
        <div className="mt-2 flex gap-1.5">
          <PopButton size="sm" tone="white" onClick={() => act(() => api.patch(`/contact/messages/${m.id}`, { read: !m.read }))} data-testid={`message-toggle-read-${m.id}`}>{m.read ? "Mark unread" : "Mark read"}</PopButton>
          <PopButton size="sm" tone="white" onClick={() => window.confirm("Delete this message?") && act(() => api.delete(`/contact/messages/${m.id}`))} aria-label="Delete message" data-testid={`message-delete-${m.id}`}><Trash2 className="h-4 w-4" /></PopButton>
        </div>
      </div>
    </Panel>
  );
}

export default function MessagesPage() {
  const { user } = useAuth();
  useSeo({ title: "Messages | DITHER", description: "Contact form inbox.", noindex: true });
  const [data, setData] = useState<{ items: Msg[]; unread: number } | null>(null);
  const load = useCallback(() => { api.get("/contact/messages").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const isAdmin = user && user.role === "admin";
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  if (user === null) return null;
  if (!isAdmin) return <PageShell title="Messages" testId="messages-page"><Panel><p className="font-bold" data-testid="messages-forbidden">This inbox is for the site admin. <Link to="/auth" className="underline">Sign in</Link></p></Panel></PageShell>;
  return (
    <PageShell title="Messages" subtitle={data ? `${data.items.length} message${data.items.length === 1 ? "" : "s"} · ${data.unread} unread` : "Loading…"} testId="messages-page">
      <div className="space-y-2" data-testid="messages-list">
        {data?.items.map((m) => <MessageCard key={m.id} m={m} onChange={load} />)}
        {data && !data.items.length && <Panel><p className="font-bold" data-testid="messages-empty">No messages yet.</p></Panel>}
      </div>
    </PageShell>
  );
}
